import { z } from 'zod';
import {
  VideoPreferencesSchema,
  CitationSchema,
  ValidationErrorSchema,
} from './generation';

// ==============================================================================
// GENERATION SESSION
// ==============================================================================

/**
 * Generation session - groups versions together
 */
export const GenerationSessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string(),
  projectId: z.string().uuid().nullable(),
  title: z.string().max(255).nullable(),
  description: z.string().max(1000).nullable(),
  currentVersionId: z.string().uuid().nullable(),
  versionCount: z.number().int().nonnegative(),
  totalTokensUsed: z.number().int().nonnegative(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
  deletedAt: z.number().int().nullable(),
});

export type GenerationSession = z.infer<typeof GenerationSessionSchema>;

/**
 * Database row type for generation_session
 */
export interface GenerationSessionRow {
  id: string;
  user_id: string;
  project_id: string | null;
  title: string | null;
  description: string | null;
  current_version_id: string | null;
  version_count: number;
  total_tokens_used: number;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

// ==============================================================================
// GENERATION VERSION
// ==============================================================================

/**
 * Generation version - single iteration (LINEAR versioning: 1, 2, 3...)
 */
export const GenerationVersionSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  userId: z.string(),

  // Linear versioning
  versionNumber: z.number().int().positive(),
  parentVersionId: z.string().uuid().nullable(),
  isEdit: z.boolean(),

  // Input
  prompt: z.string().min(1).max(30000),
  editInstruction: z.string().max(30000).nullable(),

  // Output
  code: z.string(),
  componentName: z.string(),
  explanation: z.string().nullable(),

  // Preferences
  preferences: VideoPreferencesSchema,

  // Validation
  isValid: z.boolean(),
  validationErrors: z.array(ValidationErrorSchema),

  // AI metadata
  citations: z.array(CitationSchema),
  tokensInput: z.number().int().nonnegative(),
  tokensOutput: z.number().int().nonnegative(),
  tokensTotal: z.number().int().nonnegative(),
  generationTimeMs: z.number().int().nonnegative(),
  modelVersion: z.string(),

  // Timestamp
  createdAt: z.number().int(),
});

export type GenerationVersion = z.infer<typeof GenerationVersionSchema>;

/**
 * Version summary (without code) for timeline display
 */
export const GenerationVersionSummarySchema = GenerationVersionSchema.omit({
  code: true,
  explanation: true,
  validationErrors: true,
  citations: true,
});

export type GenerationVersionSummary = z.infer<typeof GenerationVersionSummarySchema>;

/**
 * Database row type for generation_version
 */
export interface GenerationVersionRow {
  id: string;
  session_id: string;
  user_id: string;
  version_number: number;
  parent_version_id: string | null;
  is_edit: number; // 0 or 1
  prompt: string;
  edit_instruction: string | null;
  code: string;
  component_name: string;
  explanation: string | null;
  preferences: string; // JSON
  is_valid: number; // 0 or 1
  validation_errors: string; // JSON
  citations: string; // JSON
  tokens_input: number;
  tokens_output: number;
  tokens_total: number;
  generation_time_ms: number;
  model_version: string;
  created_at: number;
}

// ==============================================================================
// API REQUEST SCHEMAS
// ==============================================================================

/**
 * Create session request
 */
export const CreateSessionRequestSchema = z.object({
  projectId: z.string().uuid().optional(),
  title: z.string().max(255).optional(),
  description: z.string().max(1000).optional(),
});

export type CreateSessionRequest = z.infer<typeof CreateSessionRequestSchema>;

/**
 * Create new generation (first version)
 */
export const CreateGenerationRequestSchema = z.object({
  prompt: z.string().min(10).max(30000),
  projectId: z.string().uuid().optional(),
  sessionTitle: z.string().max(255).optional(),
  preferences: VideoPreferencesSchema.optional(),
});

export type CreateGenerationRequest = z.infer<typeof CreateGenerationRequestSchema>;

/**
 * Edit existing generation (creates new version)
 */
export const EditGenerationRequestSchema = z.object({
  sessionId: z.string().uuid(),
  editInstruction: z.string().min(5).max(2000),
  preferences: VideoPreferencesSchema.optional(),
});

export type EditGenerationRequest = z.infer<typeof EditGenerationRequestSchema>;

/**
 * Regenerate from version
 */
export const RegenerateRequestSchema = z.object({
  sessionId: z.string().uuid(),
  fromVersionId: z.string().uuid().optional(),
  newPrompt: z.string().min(10).max(30000).optional(),
  preferences: VideoPreferencesSchema.optional(),
});

export type RegenerateRequest = z.infer<typeof RegenerateRequestSchema>;

/**
 * Rollback to version
 */
export const RollbackRequestSchema = z.object({
  sessionId: z.string().uuid(),
  targetVersionId: z.string().uuid(),
});

export type RollbackRequest = z.infer<typeof RollbackRequestSchema>;

/**
 * Unified Generate Request - handles both NEW and EDIT in one endpoint
 *
 * Flow:
 * - If sessionId is provided AND prompt looks like an edit → EDIT mode
 * - If sessionId is provided AND forceNew=true → NEW mode (new version from scratch)
 * - If no sessionId → NEW mode (creates new session)
 *
 * Intent Detection:
 * - System auto-detects based on keywords (make it red, change, fix, etc.)
 * - Can be overridden with explicit isEdit flag
 */
export const UnifiedGenerateRequestSchema = z.object({
  // The full prompt to send to AI (may include injected requirements)
  prompt: z.string().min(3).max(30000),

  // The original user prompt for display/storage (without injected requirements)
  // If not provided, falls back to prompt
  userPrompt: z.string().min(3).max(30000).optional(),

  // Session context (optional)
  sessionId: z.string().uuid().optional(),

  // Which version to edit FROM (optional - defaults to session's currentVersion)
  // Use this when user clicks on a specific version in timeline and wants to edit from there
  fromVersionId: z.string().uuid().optional(),

  // Explicit intent override (optional - system will auto-detect if not provided)
  isEdit: z.boolean().optional(),

  // Force new generation even with existing session
  forceNew: z.boolean().optional().default(false),

  // Project association (optional)
  projectId: z.string().uuid().optional(),

  // Video preferences (optional - uses defaults or inherits from parent version)
  preferences: VideoPreferencesSchema.optional(),

  // Session title for new sessions (optional)
  sessionTitle: z.string().max(255).optional(),
});

export type UnifiedGenerateRequest = z.infer<typeof UnifiedGenerateRequestSchema>;

/**
 * Unified Generate Response
 */
export const UnifiedGenerateResponseSchema = z.object({
  // The created version
  version: GenerationVersionSchema,

  // The session (new or existing)
  session: GenerationSessionSchema,

  // Intent that was used
  detectedIntent: z.enum(['new', 'edit']),

  // Intent detection confidence (0-1)
  intentConfidence: z.number().min(0).max(1),

  // Whether a new session was created
  isNewSession: z.boolean(),
});

export type UnifiedGenerateResponse = z.infer<typeof UnifiedGenerateResponseSchema>;

/**
 * Update session active version request
 */
export const UpdateActiveVersionRequestSchema = z.object({
  versionId: z.string().uuid(),
});

export type UpdateActiveVersionRequest = z.infer<typeof UpdateActiveVersionRequestSchema>;

// ==============================================================================
// API RESPONSE SCHEMAS
// ==============================================================================

/**
 * Session with current version
 */
export const SessionWithCurrentVersionSchema = GenerationSessionSchema.extend({
  currentVersion: GenerationVersionSchema.nullable(),
});

export type SessionWithCurrentVersion = z.infer<typeof SessionWithCurrentVersionSchema>;

/**
 * Session with timeline
 */
export const SessionWithTimelineSchema = GenerationSessionSchema.extend({
  versions: z.array(GenerationVersionSummarySchema),
  currentVersion: GenerationVersionSchema.nullable(),
});

export type SessionWithTimeline = z.infer<typeof SessionWithTimelineSchema>;

/**
 * Version history (paginated)
 */
export const VersionHistoryResponseSchema = z.object({
  sessionId: z.string().uuid(),
  versions: z.array(GenerationVersionSummarySchema),
  totalCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  currentVersionId: z.string().uuid().nullable(),
});

export type VersionHistoryResponse = z.infer<typeof VersionHistoryResponseSchema>;

/**
 * User generation stats
 */
export const UserGenerationStatsSchema = z.object({
  totalSessions: z.number().int().nonnegative(),
  totalVersions: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  lastGenerationAt: z.number().int().nullable(),
});

export type UserGenerationStats = z.infer<typeof UserGenerationStatsSchema>;

/**
 * Current version preview for gallery view (minimal fields)
 */
export const CurrentVersionPreviewSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  componentName: z.string(),
  preferences: VideoPreferencesSchema,
});

export type CurrentVersionPreview = z.infer<typeof CurrentVersionPreviewSchema>;

/**
 * Session gallery item - session with optional current version preview
 */
export const SessionGalleryItemSchema = GenerationSessionSchema.extend({
  currentVersion: CurrentVersionPreviewSchema.nullable().optional(),
});

export type SessionGalleryItem = z.infer<typeof SessionGalleryItemSchema>;

// ==============================================================================
// ROW CONVERTERS
// ==============================================================================

/**
 * Convert database row to GenerationSession
 */
export function rowToGenerationSession(row: GenerationSessionRow): GenerationSession {
  return {
    id: row.id,
    userId: row.user_id,
    projectId: row.project_id,
    title: row.title,
    description: row.description,
    currentVersionId: row.current_version_id,
    versionCount: row.version_count,
    totalTokensUsed: row.total_tokens_used,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at,
  };
}

/**
 * Convert database row to GenerationVersion
 */
export function rowToGenerationVersion(row: GenerationVersionRow): GenerationVersion {
  // Safe JSON parse with fallback for null/undefined values
  const safeJsonParse = <T>(value: string | null | undefined, fallback: T): T => {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  return {
    id: row.id,
    sessionId: row.session_id,
    userId: row.user_id,
    versionNumber: row.version_number,
    parentVersionId: row.parent_version_id,
    isEdit: Boolean(row.is_edit),
    prompt: row.prompt,
    editInstruction: row.edit_instruction,
    code: row.code,
    componentName: row.component_name,
    explanation: row.explanation,
    preferences: safeJsonParse(row.preferences, { fps: 30, width: 1920, height: 1080, durationInSeconds: 10 }),
    isValid: Boolean(row.is_valid),
    validationErrors: safeJsonParse(row.validation_errors, []),
    citations: safeJsonParse(row.citations, []),
    tokensInput: row.tokens_input,
    tokensOutput: row.tokens_output,
    tokensTotal: row.tokens_total,
    generationTimeMs: row.generation_time_ms,
    modelVersion: row.model_version,
    createdAt: row.created_at,
  };
}

/**
 * Convert database row to GenerationVersionSummary
 */
export function rowToGenerationVersionSummary(
  row: GenerationVersionRow
): GenerationVersionSummary {
  // Safe JSON parse with fallback for null/undefined values
  const safeJsonParse = <T>(value: string | null | undefined, fallback: T): T => {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  return {
    id: row.id,
    sessionId: row.session_id,
    userId: row.user_id,
    versionNumber: row.version_number,
    parentVersionId: row.parent_version_id,
    isEdit: Boolean(row.is_edit),
    prompt: row.prompt,
    editInstruction: row.edit_instruction,
    componentName: row.component_name,
    preferences: safeJsonParse(row.preferences, { fps: 30, width: 1920, height: 1080, durationInSeconds: 10 }),
    isValid: Boolean(row.is_valid),
    tokensInput: row.tokens_input,
    tokensOutput: row.tokens_output,
    tokensTotal: row.tokens_total,
    generationTimeMs: row.generation_time_ms,
    modelVersion: row.model_version,
    createdAt: row.created_at,
  };
}
